import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h1", null, "Bulk policy edit", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h3", null, [
  /*#__PURE__*/_createTextVNode("Download "),
  /*#__PURE__*/_createElementVNode("a", { href: "/bulk.csv" }, "sample file"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("br", null, null, -1)

import { MdmDeviceSchema, MdmPolicySchema, MdmDeviceInputSchema } from '@/generated/sdk'

import * as Papa from 'papaparse'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

interface CSVEntry {
  rowNumber: number
  type: 'imei' | 'serial'
  reference: string
  policy: string
}

// Possible result states after processing rows
enum CSVPreviewState {
  DeviceNotFound,
  MultipleDevicesFound,
  PolicyNotFound,
  DevicePolicyNotFound,
  IncorrectPolicy,
  InvalidRow,
  OK,
}

// Result after processing the rows (before actual execution)
interface CSVPreviewEntry {
  rowNumber: number
  entry: CSVEntry
  message: string
  matchedDevices?: MdmDeviceSchema[]
  matchedPolicy?: MdmPolicySchema
  devicePolicy?: MdmPolicySchema
  state: CSVPreviewState
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PolicyEditBulkPage',
  setup(__props) {

const store  = useStore()

// The raw csv row
const previewEntries =  ref<CSVPreviewEntry[]>(null)
const error =  ref<string>(null)

const mdmPolicies = computed<MdmPolicySchema[]>( ()=> store.state.policies.mdmPolicies)

const mdmDevices = computed<MdmDeviceSchema[]>( ()=> store.state.devices.mdmDevices)

function upload({ files }: { files: File[] }) {
  for (const file of files) {
    Papa.parse<CSVEntry>(file, {
      header: true,
      error: (err, file) =>
      {
        console.error(err)
        error.value = err.message
      },
      complete: (parsed) =>
      {
        handleCSV(parsed.data)
          .then((res) => previewEntries.value = res)
          .catch((e) => console.error('Error handling CSV:', e))
      },
    })
  }
}

async function handleCSV(entries: CSVEntry[]): Promise<CSVPreviewEntry[]> {
  
  const search = entries
    .filter(e => isValidEntry(e))
    .map(e =>
      ({ 
        [e.type]: e.reference, 
        enterpriseId: store.state.app.enterprise.id,
      }),
    )

  await store.dispatch('loadMdmDevices', { 
    input: search,
    queryArgs: { 
      size: 10000, 
      page: 1,
    },
  })

  return entries.map((e, index) => toPreview(e, index))
}

function toPreview(entry: CSVEntry, index: number): CSVPreviewEntry {
  const matchedPolicy = mdmPolicies.value.find(p => p.name === entry.policy || p.id === entry.policy)
  const rowNumber = index + 1

  if (!isValidEntry(entry)){
    return {
      rowNumber,
      entry,
      state: CSVPreviewState.InvalidRow,
      message: 'Invalid row',
    }
  }

  if (!matchedPolicy) {
    return {
      rowNumber,
      entry,
      state: CSVPreviewState.PolicyNotFound,
      message: `Policy not found (${entry.policy})`,
    }
  }
  
  const matchedDevices = mdmDevices.value.filter(d =>
    d[entry.type] === entry.reference,
  )

  if (matchedDevices.length === 0) {
    return {
      rowNumber,
      entry,
      matchedDevices,
      matchedPolicy,
      state: CSVPreviewState.DeviceNotFound,
      message: `Device not found (${entry.type}: "${entry.reference}")`,
    }
  }

  if (matchedDevices.length > 1) {
    return {
      rowNumber,
      entry,
      matchedDevices,
      matchedPolicy,
      state: CSVPreviewState.MultipleDevicesFound,
      message: `Multiple devices found (${matchedDevices.map(d => formatId(d.googleId)).join(' - ')})`,
    }
  }

  const devicePolicy = mdmPolicies.value.find(p => p.googleId === matchedDevices[0].policyName)

  if (!devicePolicy) {
    return {
      rowNumber,
      entry,
      matchedDevices,
      matchedPolicy,
      state: CSVPreviewState.DevicePolicyNotFound,
      message: `Device policy not found (${matchedDevices[0].policyName})`,
    }
  }

  if (devicePolicy.id !== matchedPolicy.id) {
    return {
      rowNumber,
      entry,
      matchedDevices,
      matchedPolicy,
      devicePolicy,
      state: CSVPreviewState.IncorrectPolicy,
      message: `Incorrect policy (expected: "${matchedPolicy.name}" got: "${devicePolicy.name}")`,
    }
  }

  return {
    rowNumber,
    entry,
    matchedDevices,
    matchedPolicy,
    devicePolicy,
    state: CSVPreviewState.OK,
    message:'OK',
  }
}

async function continueProcessing(){
  const toUpdate = previewEntries.value
    .filter(p => p.state === CSVPreviewState.IncorrectPolicy)
    .map(t => ({
      id: t.matchedDevices[0].id,
      googleId: t.matchedDevices[0].googleId,
      policyName: t.matchedPolicy.googleId,
    } as MdmDeviceSchema))

  if(toUpdate.length === 0){
    window.alert('No devices will be updated directly')
  } else if (window.confirm(`Are you sure you want to directly update the policy of ${toUpdate.length} devices?`)) {
    await store.dispatch('updateMdmDevices', toUpdate)
  } else {
    return
  }

  const toInsert = previewEntries.value
    .filter(p => p.state === CSVPreviewState.DeviceNotFound)
    .map(t => ({
      enterpriseId: store.state.app.enterprise.id,
      expectedPolicyName: t.matchedPolicy.googleId,
      imei: t.entry.type === 'imei' ? t.entry.reference : undefined,
      serial: t.entry.type === 'serial' ? t.entry.reference : undefined,
    } as MdmDeviceInputSchema))


  if(toInsert.length === 0){
    window.alert('No devices will preloaded')
  } else if (window.confirm(`Are you sure you want to preload the policy of ${toInsert.length} devices?`)) {
    await store.dispatch('createMdmDevices', toInsert)
  } else {
    return
  }
    
  previewEntries.value = null
}

function rowClass(data: CSVPreviewEntry) {
  return {
    [CSVPreviewState.OK] : 'mdm-success',
    [CSVPreviewState.IncorrectPolicy] : 'mdm-warn',
  }[data.state] || 'mdm-danger'
}

function formatId(id: string): string {
  return id.split('/')[3]
}

function isValidEntry(e: CSVEntry) {
  return ['imei', 'serial'].includes(e.type.toLowerCase()) && e.reference
}


return (_ctx: any,_cache: any) => {
  const _component_FileUpload = _resolveComponent("FileUpload")!
  const _component_PrimeColumn = _resolveComponent("PrimeColumn")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_DodoButton = _resolveComponent("DodoButton")!
  const _component_DodoRow = _resolveComponent("DodoRow")!

  return (_openBlock(), _createElementBlock("div", null, [
    _hoisted_1,
    _hoisted_2,
    _hoisted_3,
    _hoisted_4,
    _createVNode(_component_FileUpload, {
      "custom-upload": true,
      accept: "text/csv",
      onUploader: upload
    }),
    _hoisted_5,
    (previewEntries.value)
      ? (_openBlock(), _createBlock(_component_DataTable, {
          key: 0,
          value: previewEntries.value,
          "responsive-layout": "scroll",
          paginator: true,
          rows: 10,
          "paginator-template": "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
          "rows-per-page-options": [10,25,100],
          "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
          "row-class": rowClass
        }, {
          default: _withCtx(() => [
            _createVNode(_component_PrimeColumn, {
              field: "rowNumber",
              header: "Row",
              sortable: true
            }),
            _createVNode(_component_PrimeColumn, {
              field: "entry.type",
              header: "Type",
              sortable: true
            }),
            _createVNode(_component_PrimeColumn, {
              field: "entry.reference",
              header: "Reference",
              sortable: true
            }),
            _createVNode(_component_PrimeColumn, {
              field: "message",
              header: "Result",
              sortable: true
            })
          ]),
          _: 1
        }, 8, ["value"]))
      : _createCommentVNode("", true),
    _hoisted_6,
    (previewEntries.value)
      ? (_openBlock(), _createBlock(_component_DodoRow, {
          key: 1,
          gap: "s"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_DodoButton, {
              color: "info",
              variant: "solid",
              onClick: continueProcessing
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Continue ")
              ]),
              _: 1
            }),
            _createVNode(_component_DodoButton, {
              color: "info",
              onClick: _cache[0] || (_cache[0] = () => previewEntries.value = null)
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Cancel ")
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})